import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  Building2,
  Edit3,
  RefreshCw,
  Target,
} from "lucide-react";
import {
  card,
  cardInteractive,
  pageTitle,
  microLabel,
  microLabelWide,
  accentBarSection,
  bodySecondary,
  motionFadeUp,
  motionStaggerChild,
  motionHoverLift,
  emptyState,
  iconSize,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import { listPrograms } from "../../../../services/program.service";
import ProgramForm from "./ProgramForm";

const ProgramsView = ({ onSelectProgram }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const data = await listPrograms({ isActive: true });
      setPrograms(data.programs || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load programs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  const handleFormClose = (reloadAfter) => {
    setShowForm(false);
    setEditingProgram(null);
    if (reloadAfter) fetchPrograms();
  };

  const filtered = programs.filter((p) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(q) ||
      (p.code || "").toLowerCase().includes(q) ||
      (p.department || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-4 sm:p-8 lg:p-10 pt-16 lg:pt-10 min-h-screen bg-gray-50 dark:bg-gray-900">
      <motion.div {...motionFadeUp} className="max-w-7xl mx-auto">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-2">
          <div>
            <div className={microLabelWide}>NBA Accreditation</div>
            <h1 className={cn(pageTitle, "text-3xl sm:text-4xl mt-1")}>
              Programs &amp; Outcomes
            </h1>
            <div className={cn(accentBarSection, "mt-4")} />
            <p className={cn(bodySecondary, "mt-4 max-w-2xl")}>
              Define each academic program, seed the 12 GAPC Program Outcomes,
              author program-specific outcomes &amp; educational objectives,
              and set attainment targets — the static spine NBA Criterion 3 / 8
              evidence is built on.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchPrograms}
              className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={iconSize.md} />
            </button>
            <button
              onClick={() => {
                setEditingProgram(null);
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity shadow-sm"
            >
              <Plus className={iconSize.md} />
              <span>New Program</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-8 mb-6 relative max-w-md">
          <Search
            className={cn(
              iconSize.md,
              "absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            )}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, code, or department"
            className="w-full pl-10 pr-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors"
          />
        </div>

        {/* Content */}
        {loading ? (
          <ProgramGridSkeleton />
        ) : error ? (
          <div className={cn(emptyState, "border-red-200 dark:border-red-700")}>
            <div className="text-red-600 dark:text-red-400 font-semibold">
              {error}
            </div>
            <button
              onClick={fetchPrograms}
              className="mt-3 text-sm underline text-gray-600 dark:text-gray-300"
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            onCreate={() => setShowForm(true)}
            hasSearch={!!searchTerm}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((program, i) => (
              <ProgramCard
                key={program._id}
                program={program}
                index={i}
                onSelect={() =>
                  onSelectProgram && onSelectProgram(program._id)
                }
                onEdit={() => {
                  setEditingProgram(program);
                  setShowForm(true);
                }}
              />
            ))}
          </div>
        )}
      </motion.div>

      {showForm && (
        <ProgramForm program={editingProgram} onClose={handleFormClose} />
      )}
    </div>
  );
};

// ---------- Sub-components ----------

const ProgramCard = ({ program, index, onEdit, onSelect }) => (
  <motion.div
    {...motionStaggerChild(index, 0.05)}
    whileHover={motionHoverLift}
    onClick={onSelect}
    className={cn(cardInteractive, "p-5 group cursor-pointer")}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="h-1 w-8 bg-accent1 rounded-sm" />
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700"
        title="Edit"
        aria-label="Edit program"
      >
        <Edit3 className={iconSize.sm} />
      </button>
    </div>

    <div className={microLabel}>{program.code}</div>
    <h3 className="mt-1 text-base font-bold text-gray-900 dark:text-white line-clamp-2">
      {program.name}
    </h3>

    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
      {program.department && (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
          <Building2 className="h-3 w-3" /> {program.department}
        </span>
      )}
      <span className="px-2.5 py-1 rounded-sm bg-accent2 text-accent1 font-semibold">
        {program.degreeType}
      </span>
      <span className="px-2.5 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
        {program.durationYears}yr · {program.totalSemesters} sem
      </span>
    </div>

    {program.description && (
      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
        {program.description}
      </p>
    )}

    <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider font-bold">
      Click to manage POs · PSOs · PEOs · Targets · Courses
    </div>
  </motion.div>
);

const EmptyState = ({ onCreate, hasSearch }) => (
  <div className={emptyState}>
    <div className="mx-auto h-14 w-14 rounded-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
      <Target className="h-7 w-7 text-gray-400" />
    </div>
    <div className="font-bold text-gray-700 dark:text-gray-200">
      {hasSearch ? "No programs match that search" : "No programs yet"}
    </div>
    <div className={cn(bodySecondary, "mt-1")}>
      {hasSearch
        ? "Try a different keyword."
        : "Set up your first academic program to begin authoring outcomes."}
    </div>
    {!hasSearch && (
      <button
        onClick={onCreate}
        className="mt-5 inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity"
      >
        <Plus className={iconSize.md} />
        Create program
      </button>
    )}
  </div>
);

const ProgramGridSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div key={i} className={cn(card, "p-5")}>
        <div className="h-1 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
        <div className="h-3 w-16 mt-3 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
        <div className="h-5 w-2/3 mt-2 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
        <div className="mt-4 flex gap-2">
          <div className="h-6 w-16 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
          <div className="h-6 w-12 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
        </div>
        <div className="mt-5 h-3 w-1/2 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
      </div>
    ))}
  </div>
);

export default ProgramsView;
